/**
 * The app's bell glyphs, in one place so the admin bell (`NavBell`) and the
 * employee bell (`EmployeeNotificationBell`) can never drift apart.
 *
 * - `BellIcon` — the bell everywhere on desktop and in the admin console.
 * - `EssPhoneBellIcon` — the "WagePro Phone" design's bell, used only by the
 *   employee app bar below `lg` (sized to 22px by `.wp-ess-bell svg` in
 *   styles/wagepro-theme.css).
 *
 * Inline SVG, `currentColor`, decorative (`aria-hidden`): the button around
 * each carries the accessible name.
 */

interface BellIconProps {
  size?: number;
  className?: string;
}

export function BellIcon({
  size = 18,
  className,
}: BellIconProps = {}): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export function EssPhoneBellIcon({
  className,
}: Pick<BellIconProps, 'className'> = {}): React.JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  );
}

/**
 * The cross on the notification panel's close button. Lives with the bell
 * glyphs because the panel is the only place it is drawn; same stroke
 * weight, so the two read as one set in the panel header.
 */
export function CloseIcon({
  size = 16,
  className,
}: BellIconProps = {}): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
